<template>
  <svg
    fill="currentColor"
    class="a-icon"
    :data-icon="icon"
    :class="iconClasses"
    :style="iconStyle"
    :viewBox="viewBox"
    aria-hidden="true"
    v-html="svg" />
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import type { FetchError } from "ofetch";
import { $iconResource } from "../../../resources/useIconResource";
import { useIconResource, cssValueOrSizeVar } from "#imports";

const errorSvg = "<path d=\"M256 48a208 208 0 1 1 0 416 208 208 0 1 1 0-416zm0 464A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c-13.3 0-24 10.7-24 24V264c0 13.3 10.7 24 24 24s24-10.7 24-24V152c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z\" />";
const missingIconSvg = "<path d=\"M464 256A208 208 0 1 0 48 256a208 208 0 1 0 416 0zM0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zm169.8-90.7c7.9-22.3 29.1-37.3 52.8-37.3h58.3c34.9 0 63.1 28.3 63.1 63.1c0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6c-13.3 0-24-10.7-24-24V250.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1c0-8.4-6.8-15.1-15.1-15.1H222.6c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l.4-1.2zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z\" />";
const defaultViewBox = [0, 0, 512, 512];

const props = withDefaults( defineProps<{
  /**
   * The icon alias, typically in a format like fa:classic:cake
   */
  icon: string;
  /**
   * An alias for the icon prop, for compatibility with <Nuxt-Icon> style usage.
   * If both icon and name are provided, the icon prop is preferred.
   */
  name?: string;
  size?: string | null;
  rotate?: 90 | 180 | 270 | "90" | "180" | "270" | null;
  flip?: true | false | "horizontal" | "vertical" | "both" | null;
  animation?: "spin" | "pulse" | "beat" | "fade" | null;
}>(), {
  name: undefined,
  size: null,
  rotate: null,
  flip: null,
  animation: null,
} );

const { getIcon } = useIconResource();
const iconResource = $iconResource();

const svg = computed( () => {
  if ( error.value === "broken" ) {
    return errorSvg;
  }

  if ( error.value === "missing" ) {
    return missingIconSvg;
  }

  return iconData.value?.svg ?? null;
} );

const viewBox = computed( () => {
  if ( error.value || !hasIcon.value ) {
    return defaultViewBox.join( " " );
  }

  return iconData.value?.viewBox.join( " " );
} );

const key = computed( () => {
  let key: string = props.icon || props.name || "";

  if ( key.split( ":" ).length === 2 ) {
    key = "iconify:" + key;
  }

  return key;
} );

const res = await getIcon( key.value );
const iconData = res.data;

const hasIcon = computed( () => !!iconData.value );

const iconClasses = computed( () => {
  const result = [];

  if ( error.value ) {
    result.push( "a-icon--error" );
  }

  if ( !hasIcon.value ) {
    result.push( "a-icon--missing" );
  }

  if ( props.rotate ) {
    result.push( "a-rotate-" + props.rotate );
  }

  if ( props.flip === true ) {
    result.push( "a-flip-vertical" );
  } else if ( props.flip === "horizontal" ) {
    result.push( "a-flip-horizontal" );
  } else if ( props.flip === "both" ) {
    result.push( "a-flip-both" );
  } else if ( props.flip === "vertical" ) {
    result.push( "a-flip-vertical" );
  }

  if ( props.animation ) {
    result.push( "a-" + props.animation );
  }

  return result;
} );

const iconStyle = computed( () => ( {
  height: cssValueOrSizeVar( props.size ) || undefined,
} ) );

const error = computed( () => {
  const err = ( res.error?.value as FetchError )?.statusCode;
  if ( err ) {
    if ( err === 404 ) {
      return "missing";
    } else {
      return "broken";
    }
  }
  return undefined;
} );

watch( key, async () => {
  iconData.value = await iconResource.getIcon( key.value );
} );
</script>

<style scoped lang="scss">
.a-icon {
  height: 1em;
  display: inline-block;
  vertical-align: middle;

  &--error {
    color: var(--color-danger-500);
    border: 1px dashed var(--color-danger-500);
  }

  &--missing {
    color: var(--color-warning-500);
    border: 1px dashed var(--color-warning-500);
  }
}
</style>
